import { useState, type FormEvent, type JSX } from 'react';
import { authRegister } from '@peacock/sdk-js';
import { useAuthStore } from './authStore';

/**
 * Phase-3 real user registration. Step 1 of the 2-step signup —
 * after this the user lands in the admin and gets prompted to
 * create their first Space.
 */
export function RegisterForm({
  onSwitchToLogin,
}: {
  onSwitchToLogin: () => void;
}): JSX.Element {
  const setSession = useAuthStore((s) => s.setSession);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState<
    | { kind: 'idle' }
    | { kind: 'busy' }
    | { kind: 'error'; message: string }
  >({ kind: 'idle' });

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setStatus({ kind: 'busy' });

    const baseUrl =
      (import.meta.env.VITE_PEACOCK_API_BASE_URL as string | undefined) ??
      window.location.origin;

    const result = await authRegister(baseUrl, {
      name: name.trim(),
      email: email.trim().toLowerCase(),
      password,
    });

    if (result.status === 'ok') {
      // setSession with the empty spaces[] — the admin will route to
      // the "Create your first space" view because spaces.length === 0.
      setSession(result.data.data.token, result.data.data.user, []);
      return;
    }

    if (result.status === 'error' && result.code === 422) {
      try {
        const body = (await result.response.json()) as {
          errors?: Record<string, string[]>;
        };
        const firstError = Object.values(body.errors ?? {})[0];
        setStatus({
          kind: 'error',
          message: firstError?.[0] ?? 'Validierung fehlgeschlagen',
        });
      } catch {
        setStatus({ kind: 'error', message: 'Validierung fehlgeschlagen' });
      }
      return;
    }
    if (result.status === 'error' && result.code === 409) {
      setStatus({ kind: 'error', message: 'E-Mail oder Slug bereits vergeben.' });
      return;
    }
    setStatus({
      kind: 'error',
      message:
        result.status === 'error' ? `HTTP ${result.code}` : 'Registrierung fehlgeschlagen',
    });
  }

  return (
    <main className="grid min-h-screen place-items-center bg-zinc-50 px-4">
      <form
        onSubmit={(e) => void handleSubmit(e)}
        className="w-full max-w-md rounded-xl border border-zinc-200 bg-white p-8 shadow-sm"
      >
        <header className="mb-6">
          <h1 className="text-xl font-semibold tracking-tight text-zinc-900">
            Konto anlegen
          </h1>
          <p className="mt-1 text-sm text-zinc-600">
            Kostenlos. Keine Kreditkarte. Dauert 20 Sekunden.
          </p>
        </header>

        <div className="flex flex-col gap-3">
          <label className="block">
            <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">
              Dein Name
            </span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoComplete="name"
              data-testid="register-name"
              className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">
              E-Mail
            </span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              data-testid="register-email"
              className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">
              Passwort
            </span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              data-testid="register-password"
              className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
            />
            <p className="mt-1 text-[11px] text-zinc-500">
              Mindestens 8 Zeichen. Wir hashen mit bcrypt.
            </p>
          </label>
        </div>

        {status.kind === 'error' ? (
          <p
            role="alert"
            className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900"
          >
            {status.message}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={status.kind === 'busy'}
          data-testid="register-submit"
          className="mt-6 w-full rounded-md bg-zinc-900 px-4 py-2 font-medium text-white shadow-sm disabled:cursor-not-allowed disabled:bg-zinc-300"
        >
          {status.kind === 'busy' ? 'Wird angelegt…' : 'Konto anlegen'}
        </button>

        <p className="mt-6 text-xs text-zinc-500">
          Schon ein Konto?{' '}
          <button
            type="button"
            onClick={onSwitchToLogin}
            className="text-zinc-900 underline hover:text-zinc-700"
          >
            Anmelden
          </button>
        </p>
      </form>
    </main>
  );
}
